export interface KnowledgeLink {
  label: string;
  url: string;
}

export interface KnowledgeEntry {
  id: string;
  keywords: string[];
  question: string;
  answer: string;
  category: string;
  route?: string;
  links?: KnowledgeLink[];
}

export interface Category {
  id: string;
  label: string;
}

export const categories: Category[] = [
  { id: 'inicio', label: 'Primeiros Passos' },
  { id: 'veiculos', label: 'Veículos' },
  { id: 'locacao', label: 'Locações' },
  { id: 'locatarios', label: 'Locatários' },
  { id: 'financeiro', label: 'Financeiro' },
  { id: 'manutencao', label: 'Manutenção' },
  { id: 'notas', label: 'Notas Fiscais' },
  { id: 'planos', label: 'Planos e Pagamento' },
  { id: 'relatorios', label: 'Relatórios' },
  { id: 'config', label: 'Configurações' },
];

export const knowledgeBase: KnowledgeEntry[] = [
  // ===== PRIMEIROS PASSOS =====
  {
    id: 'inicio-1',
    keywords: ['comecar', 'inicio', 'primeiro', 'passo', 'configurar', 'setup', 'novo'],
    question: 'Como começar a usar o sistema?',
    answer: '1. **Cadastre seus veículos** em Cadastros > Locação > Veículos\n2. **Cadastre seus locatários** em Cadastros > Locação > Locatários\n3. **Crie sua primeira locação** em Movimentos > Operacional > Locações\n4. **Acompanhe os pagamentos** em Movimentos > Financeiro > Pagamento\n\nAcesse a **Central de Ajuda** no menu para vídeos de treinamento.',
    category: 'inicio',
    route: '/home',
  },
  {
    id: 'inicio-2',
    keywords: ['menu', 'navegar', 'onde', 'encontrar', 'sidebar', 'lateral', 'atalho'],
    question: 'Como navegar pelo sistema?',
    answer: 'O menu lateral tem 3 áreas:\n\n- **Cadastros**: Veículos, locatários, serviços, peças, categorias, contratos\n- **Movimentos**: Locações, manutenções, pagamentos, lançamentos, multas, notas fiscais\n- **Relatórios**: Geral, financeiro e auditoria\n\nNo topo da sidebar há **atalhos rápidos** para Criar Locação, Recebimentos, Manutenção, Multas, Vistoria e Troca de Óleo.',
    category: 'inicio',
  },
  {
    id: 'inicio-3',
    keywords: ['dashboard', 'painel', 'home', 'resumo', 'visao geral'],
    question: 'O que mostra o Dashboard?',
    answer: 'O Dashboard mostra:\n\n- **Pagamentos**: Total pago, pendente e em atraso\n- **Lançamentos**: Entradas e saídas financeiras\n- **Veículos**: Quantidade e status\n- **Locações**: Ativas e encerradas\n- **Lucro por veículo**: Faturamento vs custo\n\nFiltre por: semana atual, mês, últimos 15/30 dias ou período personalizado.',
    category: 'inicio',
    route: '/home',
  },
  {
    id: 'inicio-4',
    keywords: ['treinamento', 'tutorial', 'video', 'aprender', 'ajuda', 'curso'],
    question: 'Onde encontro treinamentos e tutoriais?',
    answer: 'Acesse **Central de Ajuda** no menu lateral. Lá você encontra vídeos rápidos, explicações passo a passo e central de buscas.\n\nAlguns campos também têm um ícone do YouTube ao lado — clique para ver o tutorial daquela funcionalidade.',
    category: 'inicio',
    route: '/ajuda',
  },
  {
    id: 'inicio-5',
    keywords: ['busca', 'pesquisa', 'buscar', 'procurar', 'pesquisar', 'busca global'],
    question: 'Como usar a busca global?',
    answer: 'No topo da tela há uma **barra de pesquisa**. Ela busca rapidamente entre os principais módulos do sistema: veículos, locatários, locações, manutenções, pagamentos e mais.\n\nDigite qualquer termo e selecione o resultado para ir direto à página.',
    category: 'inicio',
  },
  {
    id: 'inicio-6',
    keywords: ['filtro', 'filtrar', 'buscar dados', 'pesquisar listagem'],
    question: 'Como usar os filtros nas listagens?',
    answer: 'Em cada listagem, você pode:\n\n- **Busca simples**: Campo de texto no topo que filtra todas as colunas\n- **Filtros avançados**: Clique em **Filtros** para abrir dropdowns de status, datas, locação, etc.\n- **Exportar**: Qualquer resultado filtrado pode ser exportado para Excel\n\nOs filtros ficam salvos na URL — pode compartilhar o link.',
    category: 'inicio',
  },

  // ===== VEÍCULOS =====
  {
    id: 'veiculos-1',
    keywords: ['veiculo', 'carro', 'moto', 'caminhao', 'cadastrar veiculo', 'novo veiculo'],
    question: 'Como cadastrar um veículo?',
    answer: 'Vá em **Cadastros > Locação > Veículos** e clique em **Cadastrar**.\n\nO formulário tem 4 abas:\n- **Identificação**: Placa, tipo (carro/moto/caminhão), marca, modelo, ano, status\n- **Dados**: Renavam, chassi, hodômetro, licenciamento, cor\n- **Valores**: Valor FIPE (buscado automaticamente pela placa), custo, data de compra\n- **Documentação**: Foto do veículo e upload de documentos',
    category: 'veiculos',
    route: '/veiculo',
  },
  {
    id: 'veiculos-2',
    keywords: ['fipe', 'tabela fipe', 'valor veiculo', 'placa', 'buscar'],
    question: 'Como funciona a busca pela FIPE?',
    answer: 'Ao preencher a **placa** do veículo e sair do campo, o sistema busca automaticamente os dados na Tabela FIPE:\n\n- Marca, modelo e ano são preenchidos\n- O valor FIPE aparece na aba **Valores**\n\nFunciona para carros, motos e caminhões.',
    category: 'veiculos',
  },
  {
    id: 'veiculos-3',
    keywords: ['importar', 'exportar', 'excel', 'lote', 'massa'],
    question: 'Como importar/exportar veículos?',
    answer: 'Na tela de Veículos:\n\n- **Importar**: Clique em Importar e envie um arquivo Excel (.xlsx)\n- **Exportar**: Clique em Exportar, selecione as colunas e baixe o Excel\n\nA exportação funciona em todas as listagens do sistema.',
    category: 'veiculos',
    route: '/veiculo',
  },
  {
    id: 'veiculos-4',
    keywords: ['limite veiculo', 'maximo veiculo', 'quantidade', 'expandir', 'aumentar'],
    question: 'Quantos veículos posso cadastrar?',
    answer: '- **PRO**: 5 veículos inclusos (+ blocos de 5 por R$12,90/mês)\n- **Avançado**: 10 veículos inclusos (+ blocos de 5 por R$19,90/mês)\n\nPara aumentar, vá em **Planos** e clique em **Expandir Frota**: o valor é proporcional aos dias que faltam do período pago.',
    category: 'veiculos',
  },
  {
    id: 'veiculos-5',
    keywords: ['status veiculo', 'inativo', 'vendido', 'desativar'],
    question: 'Como alterar o status de um veículo?',
    answer: 'Ao editar um veículo, na aba **Identificação** há o campo **Status Veículo**. Você pode definir como ativo, inativo ou vendido.\n\nVeículos inativos não aparecem para seleção ao criar novas locações.',
    category: 'veiculos',
    route: '/veiculo',
  },
  {
    id: 'veiculos-6',
    keywords: ['licenciamento', 'documento veiculo', 'foto veiculo', 'renavam', 'chassi'],
    question: 'Como cadastrar documentação do veículo?',
    answer: 'Na aba **Dados** do veículo, informe:\n- Renavam\n- Chassi\n- Vencimento do licenciamento\n\nNa aba **Documentação**:\n- Faça upload da foto do veículo\n- Anexe documentos (PDF, imagens)\n\nO sistema alerta quando o licenciamento está próximo do vencimento.',
    category: 'veiculos',
    route: '/veiculo',
  },

  // ===== LOCAÇÕES =====
  {
    id: 'locacao-1',
    keywords: ['locacao', 'alugar', 'criar locacao', 'nova locacao', 'contrato aluguel'],
    question: 'Como criar uma locação?',
    answer: 'Vá em **Movimentos > Operacional > Locações** e clique em **Cadastrar**. O formulário tem 3 etapas:\n\n**1. Dados**: Cadastre ou selecione veículo e locatário (documento, nome, email, celular e endereço)\n\n**2. Contrato**: KM inicial, periodicidade (mensal/semanal/etc), tempo em meses, fiador e observações\n\n**3. Valores**: Valor do aluguel, caução, datas de início/término e tipo de pagamento',
    category: 'locacao',
    route: '/locacao',
  },
  {
    id: 'locacao-2',
    keywords: ['periodicidade', 'frequencia', 'pagamento mensal', 'semanal', 'quinzenal', 'diario'],
    question: 'Quais periodicidades de pagamento posso usar?',
    answer: 'Ao criar a locação, você escolhe:\n\n- **Diário**: Cobrança todo dia\n- **Semanal**: A cada 7 dias\n- **Quinzenal**: A cada 15 dias\n- **Mensal**: Todo mês\n\nO sistema gera automaticamente os pagamentos futuros com base na periodicidade.',
    category: 'locacao',
  },
  {
    id: 'locacao-3',
    keywords: ['contrato', 'modelo contrato', 'gerar contrato', 'documento', 'word', 'editor'],
    question: 'Como criar modelos de contrato?',
    answer: 'Vá em **Cadastros > Locação > Contrato**. Lá você pode:\n\n- Criar modelos de contrato em formato Word\n- Usar o **editor de contrato** integrado para personalizar o documento\n- Inserir variáveis automáticas (nome do locatário, placa, valor, datas)\n\nAo visualizar uma locação, você pode gerar o contrato a partir do modelo.',
    category: 'locacao',
    route: '/modelo-contrato',
  },
  {
    id: 'locacao-4',
    keywords: ['veiculo existente', 'veiculo novo', 'locacao veiculo', 'escolher'],
    question: 'Posso cadastrar veículo novo direto na locação?',
    answer: 'Sim! Na primeira etapa da locação, há um toggle:\n\n- **Veículo Existente**: Seleciona um já cadastrado\n- **Cadastrar Veículo**: Cadastra um novo na hora\n\nO mesmo vale para o locatário — pode criar novo ou selecionar existente.',
    category: 'locacao',
  },
  {
    id: 'locacao-5',
    keywords: ['caucao', 'garantia', 'deposito', 'fiador'],
    question: 'Como funciona a caução e o fiador?',
    answer: 'Na etapa de **Valores** da locação:\n\n- **Caução**: Valor de depósito/garantia cobrado do locatário\n- **Fiador**: Selecionado na etapa Contrato\n\nAmbos são opcionais.',
    category: 'locacao',
  },
  {
    id: 'locacao-6',
    keywords: ['encerrar', 'finalizar', 'fechar locacao', 'devolver', 'status locacao'],
    question: 'Como encerrar ou alterar o status da locação?',
    answer: 'Na listagem de Locações, clique nas ações da locação desejada.\n\nStatus possíveis:\n- **Ativa**: Locação em andamento\n- **Inativa**: Pausada temporariamente\n- **Encerrada**: Finalizada\n\nAo encerrar, o sistema verifica pagamentos pendentes e libera o veículo.',
    category: 'locacao',
    route: '/locacao',
  },
  {
    id: 'locacao-7',
    keywords: ['locacao andamento', 'ja existe', 'locacao existente', 'migrar'],
    question: 'Posso cadastrar uma locação já em andamento?',
    answer: 'Sim! Na etapa **Contrato**, marque a opção **"Locação em andamento"**. Isso permite registrar uma locação que já começou antes de você usar o sistema.',
    category: 'locacao',
  },

  // ===== LOCATÁRIOS =====
  {
    id: 'locatarios-1',
    keywords: ['locatario', 'cliente', 'cadastrar cliente', 'novo locatario'],
    question: 'Como cadastrar um locatário?',
    answer: 'Vá em **Cadastros > Locação > Locatários** e clique em **Cadastrar**.\n\nPreencha: tipo de documento (CPF/CNPJ), documento, RG, nome, email, celular, endereço completo e dados da CNH (número e vencimento).',
    category: 'locatarios',
    route: '/locatarios',
  },
  {
    id: 'locatarios-2',
    keywords: ['habilitacao', 'cnh', 'vencida', 'vencimento'],
    question: 'O sistema avisa sobre CNH vencida?',
    answer: 'Sim! O sistema monitora o **vencimento da habilitação**. Quando a CNH vence, o status muda para "Habilitação Vencida" e aparece destacado na listagem.',
    category: 'locatarios',
  },
  {
    id: 'locatarios-3',
    keywords: ['app locatario', 'aplicativo', 'celular', 'mobile', 'baixar app'],
    question: 'Como o locatário usa o app?',
    answer: 'O **APP do Locatário** permite que seus clientes pelo celular:\n\n- Vejam histórico de pagamentos e copiem PIX\n- Visualizem o contrato\n- Enviem vistorias com fotos\n- Registrem trocas de óleo\n\n**Como criar o acesso**: Na tela de Locatários, clique nos 3 pontinhos > **Login no APP**.\n\nDisponível na Play Store e App Store.',
    category: 'locatarios',
    route: '/app-locatario',
  },
  {
    id: 'locatarios-4',
    keywords: ['login app', 'acesso app', 'senha app', 'criar acesso'],
    question: 'Como criar o login do locatário no app?',
    answer: 'Na tela de **Locatários**, encontre o locatário desejado e clique no menu de ações (3 pontinhos). Selecione **"Login no APP"**.\n\nVocê irá digitar o usuário e senha para o locatário e clicar em **Salvar**. Com essas credenciais, o locatário poderá acessar o aplicativo.',
    category: 'locatarios',
    route: '/locatarios',
    links: [
      { label: 'Baixar na Play Store', url: 'https://play.google.com/store/apps/details?id=com.locasmartpro.gestaodelocadoras&pcampaignid=web_share' },
      { label: 'Baixar na App Store', url: 'https://apps.apple.com/br/app/locasmartpro-app-locat%C3%A1rio/id6757689416' },
    ],
  },

  // ===== FINANCEIRO =====
  {
    id: 'financeiro-1',
    keywords: ['pagamento', 'recebimento', 'cobrar', 'receber', 'pendente', 'pago', 'atraso'],
    question: 'Como acompanhar pagamentos?',
    answer: 'Vá em **Movimentos > Financeiro > Pagamento**. Cada pagamento tem status:\n\n- **Pago** (verde): Recebido\n- **Pendente** (amarelo): Aguardando\n- **Vencido** (vermelho): Em atraso\n\nAções disponíveis:\n- Confirmar pagamento rápido\n- Emitir nota fiscal (NFSe)\n- Baixar recibo\n- Enviar cobrança por WhatsApp\n- Cancelar ou parcelar',
    category: 'financeiro',
    route: '/pagamento',
  },
  {
    id: 'financeiro-2',
    keywords: ['lancamento', 'despesa', 'receita', 'entrada', 'saida', 'financeiro'],
    question: 'Como registrar receitas e despesas?',
    answer: 'Vá em **Movimentos > Financeiro > Lançamento** e clique em Cadastrar.\n\n**4 abas**:\n1. **Dados**: Valor, tipo (Entrada ou Saída), status (Resolvido/Pendente), datas\n2. **Detalhamento**: Categorias (até 2) e observação\n3. **Vínculos**: Vincular a locação e/ou veículo (opcional)\n4. **Adicionais**: Repetir em parcelas, periodicidade e anexar arquivos\n\nEntradas aparecem com seta verde e saídas com seta vermelha.',
    category: 'financeiro',
    route: '/lancamento',
  },
  {
    id: 'financeiro-3',
    keywords: ['categoria', 'categorizar', 'tipo despesa', 'tipo receita', 'cor'],
    question: 'Como criar categorias financeiras?',
    answer: 'Vá em **Cadastros > Financeiro > Categorias**.\n\nCrie categorias como "Combustível", "Seguro", "Aluguel" etc. Cada uma pode ter uma **cor** para facilitar a visualização.\n\nNos lançamentos, você pode selecionar até 2 categorias.',
    category: 'financeiro',
    route: '/categoria',
  },
  {
    id: 'financeiro-4',
    keywords: ['parcela', 'parcelar', 'repetir', 'recorrente', 'mensal fixo'],
    question: 'Como criar lançamentos recorrentes?',
    answer: 'Ao cadastrar um lançamento, na aba **Adicionais**:\n\n1. Marque "Repetir este lançamento em parcelas"\n2. Defina a **data de início**\n3. Escolha a **periodicidade** (mensal, semanal, quinzenal, diário)\n4. Informe o **número de parcelas** (1 a 12)\n\nO sistema cria automaticamente todos os lançamentos futuros.',
    category: 'financeiro',
  },
  {
    id: 'financeiro-5',
    keywords: ['whatsapp', 'cobranca', 'mensagem', 'cobrar cliente'],
    question: 'Como enviar cobrança por WhatsApp?',
    answer: 'Na tela de **Pagamentos**, clique nas ações de um pagamento pendente ou vencido e selecione **Cobrança WhatsApp**.\n\nO sistema abre o WhatsApp com uma mensagem pré-formatada para enviar ao locatário.',
    category: 'financeiro',
    route: '/pagamento',
  },
  {
    id: 'financeiro-6',
    keywords: ['recibo', 'comprovante', 'baixar recibo', 'pdf'],
    question: 'Como gerar recibo de pagamento?',
    answer: 'Na tela de **Pagamentos**, clique nas ações do pagamento desejado e selecione **Recibo**.\n\nO sistema gera um PDF com os dados do pagamento que pode ser baixado ou impresso.',
    category: 'financeiro',
    route: '/pagamento',
  },
  {
    id: 'financeiro-7',
    keywords: ['acao em lote', 'selecionar varios', 'marcar todos', 'bulk', 'massa'],
    question: 'Posso fazer ações em vários pagamentos de uma vez?',
    answer: 'Sim! Na listagem de **Pagamentos** e **Lançamentos**, você pode selecionar múltiplos itens e executar ações em lote:\n\n- Marcar como pago/pendente\n- Excluir vários de uma vez\n- Ajustar valores em múltiplos lançamentos',
    category: 'financeiro',
  },

  {
    id: 'financeiro-8',
    keywords: ['parcelar', 'parcela', 'dividir pagamento', 'parcelamento locatario'],
    question: 'Como parcelar pagamento para Locatário?',
    answer: 'Na tela de **Pagamentos**, encontre o pagamento desejado e clique no menu de ações (3 pontinhos). Selecione **"Parcelar"**.\n\nVocê poderá dividir o valor em várias parcelas, definindo:\n- **Número de parcelas**\n- **Data de vencimento** de cada parcela\n- **Valor** de cada parcela\n\nO pagamento original será substituído pelas parcelas geradas.\n\n**Obs:** Não é possível parcelar pagamentos já pagos ou cancelados.',
    category: 'financeiro',
    route: '/pagamento',
  },
  {
    id: 'financeiro-9',
    keywords: ['juros', 'multa', 'atraso', 'mora', 'automatico', 'calcular juros'],
    question: 'Como calcular juros e multa automaticamente?',
    answer: 'Na criação ou edição de uma **Locação**, na etapa de **Valores**, na seção **Juros e Multa**, configure:\n\n**Juros** (acumula enquanto estiver em atraso):\n- **Juros por dia**: Valor fixo em R$ somado a cada dia de atraso\n- **Juros ao mês**: Percentual aplicado proporcionalmente por dia\n\n**Multa** (aplicada uma única vez ao atrasar):\n- **Percentual**: % sobre o valor da parcela\n- **Valor fixo**: Valor em R$ somado à parcela\n\nA **multa** é cobrada apenas uma vez quando o pagamento vence, já os **juros** continuam acumulando diariamente até a quitação.',
    category: 'financeiro',
    route: '/locacao',
  },

  // ===== MANUTENÇÃO =====
  {
    id: 'manutencao-1',
    keywords: ['manutencao', 'servico', 'conserto', 'reparo', 'oficina', 'ordem servico'],
    question: 'Como registrar uma manutenção?',
    answer: 'Vá em **Movimentos > Operacional > Manutenções** e clique em Cadastrar.\n\nEscolha entre **Manutenção** normal ou **Ordem de Serviço**.\n\nO formulário tem abas:\n1. **Veículo**: Selecione e informe o hodômetro\n2. **Peças**: Grid editável com quantidade e valor\n3. **Serviços**: Grid com serviços e valores\n4. **Informações**: Total auto-calculado, pagamento, data e prestador\n5. **Arquivos**: Garantia, recibo e fotos',
    category: 'manutencao',
    route: '/manutencoes',
  },
  {
    id: 'manutencao-2',
    keywords: ['peca', 'estoque', 'inventario', 'pecas', 'controle estoque'],
    question: 'Como controlar estoque de peças?',
    answer: '1. **Cadastrar peças**: Em Cadastros > Manutenção > Peças (nome, valor, vida útil em KM)\n2. **Movimentar estoque**: Em Movimentos > Operacional > Estoque de Peças\n\nAo usar peças numa manutenção, o estoque é atualizado automaticamente. O nível aparece com cores (verde/amarelo/vermelho).',
    category: 'manutencao',
    route: '/estoque',
  },
  {
    id: 'manutencao-3',
    keywords: ['oleo', 'troca oleo', 'troca de oleo', 'lubrificante'],
    question: 'Como registrar troca de óleo?',
    answer: 'Vá em **Movimentos > Operacional > Troca de Óleo** e clique em Cadastrar.\n\nPreencha:\n- Locação (obrigatório)\n- Hodômetro e data da troca\n- Observação (obrigatório)\n- Fotos: painel, óleo e recibo\n- Vídeo da troca (opcional)\n\nO locatário também pode enviar pelo app — fica como "Em Análise" até você aprovar ou recusar.',
    category: 'manutencao',
    route: '/troca-de-oleo',
  },
  {
    id: 'manutencao-4',
    keywords: ['vistoria', 'checklist', 'inspecao', 'fotos veiculo', 'estado'],
    question: 'Como fazer uma vistoria?',
    answer: 'Vá em **Movimentos > Operacional > Vistoria** e clique em Cadastrar.\n\nPara **carros**, o formulário pede fotos em 3 etapas:\n- **Exterior**: Lateral esquerda/direita, frente e traseira\n- **Pneus**: 4 fotos (cada pneu)\n- **Interior**: Motor, painel, porta-malas, bancos e hodômetro\n\nPara **motos** as fotos são adaptadas.\n\nO locatário também pode enviar pelo app.',
    category: 'manutencao',
    route: '/vistoria',
  },
  {
    id: 'manutencao-5',
    keywords: ['prestador', 'fornecedor', 'mecanico', 'oficina'],
    question: 'Como cadastrar prestadores de serviço?',
    answer: 'Vá em **Cadastros > Manutenção > Prestadores / Fornecedores** e clique em Cadastrar.\n\nInforme nome, contato e endereço. Depois você seleciona o prestador ao registrar manutenções e trocas de óleo.',
    category: 'manutencao',
    route: '/prestador-servico',
  },
  {
    id: 'manutencao-6',
    keywords: ['servico cadastro', 'tipo servico', 'servicos oferecidos'],
    question: 'Como cadastrar serviços?',
    answer: 'Vá em **Cadastros > Manutenção > Serviços** e clique em Cadastrar.\n\nDefina o nome do serviço, valor padrão e período em KM (quando deve ser refeito). Esses serviços ficam disponíveis para seleção ao registrar manutenções.',
    category: 'manutencao',
    route: '/servico',
  },
  {
    id: 'manutencao-7',
    keywords: ['ordem servico', 'os', 'orcamento', 'previsao'],
    question: 'Qual a diferença entre Manutenção e Ordem de Serviço?',
    answer: '- **Manutenção**: Registro de um serviço já realizado (com data, valor, recibo)\n- **Ordem de Serviço**: Planejamento de um serviço futuro (com previsão de início e prestador)\n\nAmbos permitem adicionar peças e serviços na mesma tela.',
    category: 'manutencao',
    route: '/manutencoes',
  },

  // ===== MULTAS =====
  {
    id: 'multas-1',
    keywords: ['multa', 'infracoes', 'transito', 'registrar multa'],
    question: 'Como registrar multas de trânsito?',
    answer: 'Vá em **Movimentos > Financeiro > Multa** e clique em Cadastrar.\n\n**3 abas**:\n1. **Identificação**: Número RENAINF, auto de infração, código\n2. **Valores e Datas**: Valor, status, descrição, datas de cometimento e vencimento\n3. **Participantes**: Locação, veículo e indicação do real infrator\n\nVocê pode marcar para **não gerar lançamento financeiro** automaticamente.',
    category: 'financeiro',
    route: '/multas',
  },
  {
    id: 'multas-2',
    keywords: ['multa paga', 'pagar multa', 'quitar multa', 'cancelar multa'],
    question: 'Como alterar o status de uma multa?',
    answer: 'Na listagem de Multas, clique no menu de ações (3 pontinhos).\n\nOpções:\n- **Marcar como Paga**\n- **Cancelar**\n- **Marcar como A Vencer**\n- **Gerar Lançamento Financeiro** vinculado',
    category: 'financeiro',
    route: '/multas',
  },

  // ===== NOTAS FISCAIS =====
  {
    id: 'notas-1',
    keywords: ['nota fiscal', 'nfse', 'emitir nota', 'nota servico'],
    question: 'Como emitir uma nota fiscal?',
    answer: 'Vá em **Movimentos > Financeiro > Notas Fiscais**.\n\n**Antes de emitir**, configure em Minha Conta (aba NFSe):\n- Certificado digital (.pfx) e senha\n- Código e inscrição municipal\n- Série e número do RPS\n- Serviços com código CNAE e alíquota ISS\n\nDepois, emita notas vinculadas a pagamentos ou crie manualmente.',
    category: 'notas',
    route: '/notas-fiscais',
  },
  {
    id: 'notas-2',
    keywords: ['certificado digital', 'configurar nota', 'iss', 'nfse config', 'rps'],
    question: 'Como configurar a emissão de notas?',
    answer: 'Vá em **Minha Conta** (clique no seu nome > Minha Conta) e acesse a aba **NFSe**.\n\nConfigure:\n- Upload do certificado digital (.pfx)\n- Senha do certificado\n- Código do município\n- Inscrição municipal\n- Número e série do RPS\n- Serviços padrão com código CNAE e alíquota ISS',
    category: 'notas',
    route: '/minha-conta',
  },
  {
    id: 'notas-3',
    keywords: ['cancelar nota', 'nota cancelada', 'estornar nota'],
    question: 'Como cancelar uma nota fiscal?',
    answer: 'Na listagem de **Notas Fiscais**, encontre a nota emitida e clique nas ações para **Cancelar**.\n\nA nota muda de status para "Cancelada". Notas canceladas ficam registradas no histórico para fins de auditoria.',
    category: 'notas',
    route: '/notas-fiscais',
  },
  {
    id: 'notas-4',
    keywords: ['agendar nota', 'nota automatica', 'agendamento'],
    question: 'Posso agendar emissão de notas?',
    answer: 'Sim! Ao criar uma nota, você pode definir um agendamento para emissão futura. A nota fica com status "Agendada" até a data definida.',
    category: 'notas',
    route: '/notas-fiscais',
  },

  // ===== PLANOS =====
  {
    id: 'planos-1',
    keywords: ['plano', 'preco', 'valor', 'quanto custa', 'assinatura', 'pro', 'avancado'],
    question: 'Quais são os planos disponíveis?',
    answer: '**PRO** - R$ 79,90/mês\n- 5 veículos inclusos | 5 usuários\n- + R$ 12,90 por bloco de 5 veículos extras\n\n**Avançado** - R$ 109,90/mês\n- 10 veículos inclusos | 10 usuários\n- + R$ 19,90 por bloco de 5 veículos extras\n\n**Descontos por ciclo:**\n- Trimestral: 5% off\n- Semestral: 15% off\n- Anual: 25% off',
    category: 'planos',
    route: '/planos',
  },
  {
    id: 'planos-2',
    keywords: ['trocar plano', 'upgrade', 'mudar plano', 'melhorar', 'renovar'],
    question: 'Como trocar ou renovar o plano?',
    answer: 'Vá em **Minha Conta > Assinatura** para ver seu plano atual.\n\nPara mudar, acesse **Planos** no menu lateral, compare as opções e clique em assinar.\n\nFormas de pagamento: PIX, cartão de crédito ou boleto.',
    category: 'planos',
    route: '/planos',
  },
  {
    id: 'planos-3',
    keywords: ['cupom', 'desconto', 'codigo', 'promocao'],
    question: 'Como usar um cupom de desconto?',
    answer: 'No checkout (ao assinar ou renovar), há um campo para inserir o **código do cupom**. O desconto é aplicado automaticamente — pode ser percentual ou valor fixo.',
    category: 'planos',
  },
  {
    id: 'planos-4',
    keywords: ['pix', 'boleto', 'cartao', 'forma pagamento', 'como pagar'],
    question: 'Quais formas de pagamento são aceitas?',
    answer: 'Para assinar o plano, você pode pagar via:\n\n- **PIX**: QR Code gerado na hora\n- **Cartão de Crédito**: Cobrança recorrente automática\n- **Boleto**: Boleto bancário\n\nA cobrança é processada pelo gateway **Asaas**.',
    category: 'planos',
  },
  {
    id: 'planos-5',
    keywords: ['periodo teste', 'trial', 'gratis', 'gratuito', 'experimentar'],
    question: 'Existe período de teste gratuito?',
    answer: 'Sim! Ao se cadastrar, você recebe um **período de teste** para conhecer o sistema completo antes de assinar um plano.\n\nDurante o teste, todas as funcionalidades estão disponíveis.',
    category: 'planos',
  },

  // ===== RELATÓRIOS =====
  {
    id: 'relatorios-1',
    keywords: ['relatorio', 'gerar relatorio', 'dados', 'resumo'],
    question: 'Quais relatórios estão disponíveis?',
    answer: 'Em **Relatórios** no menu:\n\n- **Geral**: Manutenções, locações e status dos veículos\n- **Financeiro**: Receitas, despesas e fluxo de caixa\n- **Auditoria**: Histórico de todas as alterações no sistema\n\nTodos podem ser filtrados por período e exportados para Excel.',
    category: 'relatorios',
    route: '/relatorio-geral',
  },
  {
    id: 'relatorios-2',
    keywords: ['auditoria', 'historico', 'log', 'quem alterou', 'rastreabilidade'],
    question: 'Como ver quem alterou dados no sistema?',
    answer: 'Vá em **Relatórios > Auditoria**. Lá você vê:\n\n- Quem fez a alteração (usuário)\n- O que foi alterado (dados antigos vs novos)\n- Data e hora\n- IP de acesso\n\nIdeal para rastrear mudanças e manter controle da equipe.',
    category: 'relatorios',
    route: '/auditoria',
  },
  {
    id: 'relatorios-3',
    keywords: ['exportar', 'excel', 'baixar', 'planilha', 'download'],
    question: 'Como exportar dados para Excel?',
    answer: 'Em qualquer listagem, clique em **Exportar**.\n\nUm modal aparece onde você seleciona quais colunas incluir. Clique em Exportar e o arquivo Excel é baixado automaticamente.\n\nFunciona em veículos, pagamentos, lançamentos, multas e todas as outras listagens.',
    category: 'relatorios',
  },
  {
    id: 'relatorios-4',
    keywords: ['lucro', 'faturamento', 'custo veiculo', 'rentabilidade'],
    question: 'Como ver o lucro por veículo?',
    answer: 'No **Dashboard** (tela inicial), há a seção de **Lucro por Veículo** que mostra:\n\n- Faturamento de cada veículo\n- Custos (manutenção, peças, etc.)\n- Lucro líquido\n\nFiltre por período para análises mais detalhadas.',
    category: 'relatorios',
    route: '/home',
  },

  // ===== CONFIGURAÇÕES =====
  {
    id: 'config-1',
    keywords: ['usuario', 'permissao', 'acesso', 'adicionar usuario', 'colaborador'],
    question: 'Como adicionar usuários?',
    answer: 'Vá em **Cadastros > Manutenção > Usuários** e clique em Cadastrar.\n\nDefina nome, email, senha e se é **administrador**. Usuários não-admin podem ter permissões específicas por módulo.\n\nLimite: PRO = 5 usuários, Avançado = 10 usuários.',
    category: 'config',
    route: '/usuarios',
  },
  {
    id: 'config-2',
    keywords: ['minha conta', 'perfil', 'empresa', 'dados empresa', 'configuracao'],
    question: 'Onde configuro os dados da empresa?',
    answer: 'Clique no seu **nome no canto superior direito** > **Minha Conta**.\n\nLá você configura:\n- Dados da empresa\n- Assinatura e plano\n- Integração de pagamento\n- Dados para emissão de NFSe\n- Certificado digital',
    category: 'config',
    route: '/minha-conta',
  },
  {
    id: 'config-3',
    keywords: ['suporte', 'problema', 'erro', 'bug', 'contato', 'ajuda humana'],
    question: 'Como entrar em contato com o suporte?',
    answer: 'Você tem 3 opções:\n\n1. **WhatsApp**: Botão verde flutuante na tela\n2. **Suporte**: Menu lateral > Suporte\n3. **Reportar Problemas**: Menu lateral > Reportar Problemas\n\nO time responde em horário comercial.',
    category: 'config',
    route: '/suporte',
  },
  {
    id: 'config-4',
    keywords: ['notificacao', 'alerta', 'aviso', 'sino', 'lembrete'],
    question: 'Como funcionam as notificações?',
    answer: 'O ícone de **sino** no topo da tela mostra suas notificações: pagamentos vencidos, habilitações vencendo e atualizações do sistema.\n\nClique em "Ver todas" para acessar a página completa.',
    category: 'config',
    route: '/notifications',
  },
  {
    id: 'config-5',
    keywords: ['empresa', 'cadastrar empresa', 'dados cadastrais'],
    question: 'Como editar dados da empresa?',
    answer: 'Vá em **Cadastros > Manutenção > Empresas** ou acesse **Minha Conta**.\n\nLá você pode alterar nome, CNPJ, endereço e outros dados cadastrais da empresa.',
    category: 'config',
    route: '/empresas',
  },
  {
    id: 'config-6',
    keywords: ['reportar', 'problema', 'bug', 'erro', 'falha'],
    question: 'Como reportar um problema no sistema?',
    answer: 'Vá em **Reportar Problemas** no menu lateral. Descreva o problema encontrado com o máximo de detalhes.\n\nVocê também pode enviar prints pelo **WhatsApp** do suporte para agilizar o atendimento.',
    category: 'config',
    route: '/reportar-problemas',
  },
];

export function searchKnowledge(query: string): KnowledgeEntry[] {
  if (!query.trim()) return [];

  const normalizedQuery = query
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, '');

  const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 2);
  if (queryWords.length === 0) return [];

  const scored = knowledgeBase.map(entry => {
    let score = 0;
    let keywordHits = 0;
    const nk = entry.keywords.map(k => k.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
    const nq = entry.question.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    for (const word of queryWords) {
      let wordHit = false;
      for (const kw of nk) {
        if (kw === word) { score += 15; wordHit = true; }
        else if (kw.includes(word)) { score += 5; wordHit = true; }
      }
      if (nq.includes(word)) { score += 3; wordHit = true; }
      if (wordHit) keywordHits++;
    }

    // Exige alta cobertura: quanto mais palavras na query, maior a exigência
    const coverage = keywordHits / queryWords.length;
    const minCoverage = queryWords.length <= 2 ? 0.5 : 0.7;
    if (coverage < minCoverage) score = 0;

    return { entry, score };
  });

  const minScore = Math.max(15, queryWords.length * 8);
  return scored.filter(s => s.score >= minScore).sort((a, b) => b.score - a.score).slice(0, 5).map(s => s.entry);
}
